// react
import { ReactElement, useState } from 'react';

// mui
import { Box, Button, Grid } from '@mui/material';

// icons
import {
	ChevronLeft as IconChevronLeft,
	ChevronRight as IconChevronRight,
} from '@mui/icons-material';

// theme
import { theme } from '../../../theme';

// hooks
import { useTranslation } from 'react-i18next';

// props
export interface CarouselProps {
	children: ReactElement[];
	childrenVisible?: number;
	onNext?: (child: ReactElement) => void;
	onPrev?: (child: ReactElement) => void;
}

export const Carousel = ({ children, childrenVisible = 2, onNext, onPrev }: CarouselProps) => {
	// Hooks
	const { t } = useTranslation();

	// state
	const [startIndex, setStartIndex] = useState(0);

	// vars
	const gap = 2;
	const totalGaps = childrenVisible === 1 ? 0 : (childrenVisible - 1) * gap;
	const spacingToReducePerBox = totalGaps / childrenVisible;
	const boxWidth = 100 / childrenVisible;
	const maxIndex = Math.max(0, children.length - childrenVisible);

	// handlers
	const handlePrev = () => {
		setStartIndex((prev) => Math.max(prev - 1, 0));
		onPrev?.(visibleChildren[0]);
	};
	const handleNext = () => {
		setStartIndex((prev) => Math.min(prev + 1, maxIndex));
		onNext?.(visibleChildren[childrenVisible - 1]);
	};

	// slice visible children
	const visibleChildren = children.slice(startIndex, startIndex + childrenVisible);

	return (
		<Box sx={{ position: 'relative' }}>
			<Box
				sx={{
					width: '100%',
					height: '100%',
					display: 'flex',
					flexWrap: 'nowrap',
					gap,
					overflow: 'hidden',
				}}>
				{visibleChildren.map((child, i) => (
					<Box
						key={`carousel-item-${startIndex + i}`}
						sx={{
							width: `calc(${boxWidth}% - ${theme.spacing(spacingToReducePerBox)})`,
							height: '100%',
							flexShrink: 0,
							transition: 'width 0.3s',
						}}>
						{child}
					</Box>
				))}
			</Box>
			{visibleChildren.length > 0 && (
				<Box
					sx={{
						marginTop: theme.spacing(2),
						[theme.breakpoints.up('md')]: {
							position: 'absolute',
							left: 0,
							bottom: 0,
							width: '100%',
							transform: `translateY(calc(100% + ${theme.spacing(1.5)}))`,
						},
					}}>
					<Grid container justifyContent="flex-end" spacing={1}>
						<Grid item xs="auto">
							<Button
								aria-label={t('previous')}
								className="circle"
								onClick={handlePrev}
								disabled={startIndex === 0}
								size="small"
								sx={{ padding: 0.5 }}
								variant="contained">
								<IconChevronLeft />
							</Button>
						</Grid>
						<Grid item xs="auto">
							<Button
								aria-label={t('next')}
								className="circle"
								onClick={handleNext}
								disabled={startIndex >= maxIndex}
								size="small"
								sx={{ padding: 0.5 }}
								variant="contained">
								<IconChevronRight />
							</Button>
						</Grid>
					</Grid>
				</Box>
			)}
		</Box>
	);
};
